import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Button, Container, ContentPanel, ErrorSummary, FieldRow, PortalLayout, PortalPanelBody, Stack, Stepper, TextField, Typography } from "../../design-system";
import type { StepperItemData } from "../../design-system";
import { useCapability } from "../../capability/use-capability";
import { BUSINESS_DETAILS_SECTION_ID, isSectionConfirmed } from "../../routing/route-table";
import type { ConfirmedAnswers, SectionInstanceId } from "../../routing/route-table";
import { isRenewalSubmittedWriteRefusal } from "../../state/outcome";
import type { AccountReadResult } from "../../state/renewal-data-port";
import type { WorkingCopyPort } from "../../state/working-copy-port";
import { isConfirmDetailsFieldId, sanitizePhoneInput, validateConfirmDetailsField } from "../../validation/error-registry";
import { buildSaveOutcomeContent } from "../renewal-capture/outcome-content";
import { useSectionSaveLifecycle } from "../renewal-capture/section-save-lifecycle";
import { confirmDetailsContent as content, getConfirmDetailsFieldLabel } from "./content";

/**
 * The dedicated, single-section confirm-details route (TG-15,
 * ADR-20261001-rjj8 — split out of the former combined
 * confirm-details-and-change-check step, TG-13). Hosts exactly the
 * `business-details` section: no `Accordion`, no section list, no other
 * multi-section machinery — there is exactly one thing on this page.
 *
 * Every save-lifecycle concern (the double-write guard, the reopen/
 * pending-answer handling, stale/reload-failure gating, validation
 * gating) is the TG-14 extracted module's (`section-save-lifecycle.ts`,
 * ADR-20261001-evn2) — this component calls `useSectionSaveLifecycle`
 * and consumes `lifecycle.sectionFor(...)` exactly as
 * `MultiSectionStepRoute.tsx` does, never reimplementing any of it.
 *
 * Presentational, like `DeclarationRoute.tsx`: no router import, no
 * design-system-external I/O. `onSectionConfirmed`/`onSectionWriteIndeterminate`
 * are the SAME callback shape `MultiSectionStepRoute` already takes, so
 * whatever wires this route into the app (App.tsx's composition root,
 * TG-18) can reuse its existing handler for every step route alike — this
 * file names no step path and performs no navigation itself. "Continue
 * navigates to change-check" and "Save and finish later returns to the
 * landing route" are both the composition root's job: `onSectionConfirmed`
 * firing (a write that actually Succeeded) and `onSaveAndFinishLater`
 * being activated are this component's only two signals for "the customer
 * is done here," exactly the same shape `DeclarationRoute`'s own
 * `onSubmit`/`onSaveAndFinishLater` already establish.
 *
 * Capability (ADR-20260826-ejck): imports ONLY `useCapability()`, reading
 * `confirmableSectionIds` membership for this section — never a role,
 * never a claim. Where the resolved capability does not permit confirming
 * this section, the Continue control is omitted from the render entirely,
 * never rendered disabled.
 *
 * AC-3 (resume): there is no separate read-only "confirmed" display here
 * (unlike the generic multi-section renderer's collapsed-section summary,
 * which exists only to keep MULTIPLE open sections from overwhelming one
 * screen — a concern this single-section route does not have). Resuming
 * into an already-confirmed section instead seeds this same editable form
 * with its saved answer, once, on mount, via the TG-14 module's own
 * `onRequestChange` — the module's designed mechanism for "populate a
 * section's editable fields from its saved answer" (see that function's
 * own doc comment). An opened-but-never-confirmed field is left at the
 * hook's own empty-string default, never read from `confirmedAnswers`.
 */
const SECTION_ID = BUSINESS_DETAILS_SECTION_ID;

/** Exported so route-table.test.ts / ConfirmDetailsRoute.test.tsx can assert
 * this stays equal to `SECTION_TABLE`'s business-details descriptor's own
 * `fields` (OBJ-139/FIND-204) -- the two are maintained in two different
 * files for the reasons documented at this component's own doc comment
 * above, so nothing else keeps them in step without a test that reads both. */
export const FIELDS = ["businessName", "mailingAddress", "contactName", "contactPhone", "contactEmail"] as const;
const PAIRED_FIELDS: ReadonlySet<(typeof FIELDS)[number]> = new Set(["contactPhone", "contactEmail"]);

const REOPEN_ON_MOUNT = [{ sectionId: SECTION_ID, fields: FIELDS }] as const;

type FieldId = (typeof FIELDS)[number];

/** Screen3a's own `input-mode`/control-type choices
 * (`docs/ux-review/design_handoff_renewals_portal/prototypes/Screen3-RenewalFlow.dc.html`):
 * every other field is a plain text control. */
const FIELD_TYPES: Record<FieldId, "text" | "email" | "tel"> = {
  businessName: "text",
  mailingAddress: "text",
  contactName: "text",
  contactPhone: "tel",
  contactEmail: "email",
};

/**
 * AC-1/AC-14 (m1-exposure-detail-sourcing): the stored account record's
 * field names (`RenewalDataPort.readAccount`'s own shape) don't match this
 * route's own `FieldId`s one-for-one -- `contactPhone` here is
 * `phoneNumber` there -- so this maps between the two in one named place
 * rather than scattering the rename across every call site.
 */
const FIELD_TO_ACCOUNT_KEY: Record<FieldId, keyof AccountReadResult> = {
  businessName: "businessName",
  mailingAddress: "mailingAddress",
  contactName: "contactName",
  contactPhone: "phoneNumber",
  contactEmail: "contactEmail",
};

/** AC-1: "a stored account record with no contact name leaves that field
 * empty, and one with no phone number ... leaves the phone field empty" --
 * `null` (and anything that is not a string, defensively) maps to `""`,
 * never the literal string "null" or a fabricated value. */
function accountDefaultValue(accountDefaults: AccountReadResult, fieldId: FieldId): string {
  const value = accountDefaults[FIELD_TO_ACCOUNT_KEY[fieldId]];
  return typeof value === "string" ? value : "";
}

/**
 * AC-1/AC-14's per-field precedence, for the NOT-YET-CONFIRMED case only
 * (a confirmed section's value always comes from `confirmedAnswers` via
 * `onRequestChange`/`readSavedFieldValue` -- this function is never
 * consulted once a confirmation exists): the section's own
 * "Save and finish later" draft, read fresh from the server, takes
 * precedence over the stored account record, which is the final
 * fallback. Neither source is read once the customer has typed
 * something of their own in this mounted instance -- see the seeding
 * effect below, which calls this exactly once, on mount.
 */
function defaultFieldValue(
  fieldId: FieldId,
  draftAnswers: Record<string, unknown> | undefined,
  accountDefaults: AccountReadResult | undefined,
): string {
  const fromDraft = draftAnswers?.[fieldId];
  if (typeof fromDraft === "string") {
    return fromDraft;
  }
  return accountDefaults !== undefined ? accountDefaultValue(accountDefaults, fieldId) : "";
}

export interface ConfirmDetailsRouteProps {
  /** ADR-20261102-h2fd (AC-15): H2 shows the three-step stepper above screen 3's card. */
  readonly stepperItems?: readonly StepperItemData[];
  readonly renewalId: string;
  /** AC-1: the business name shown in the shared header at 600px and
   * above, read through the renewal data port by the caller -- never
   * fabricated here. */
  readonly businessName: string;
  readonly confirmedAnswers: ConfirmedAnswers;
  /**
   * AC-1/AC-14: the stored account record, read through the renewal data
   * port by the caller (App.tsx's `ConfirmDetailsRouteWrapper`) -- the
   * last-resort fallback in the per-field precedence (confirmed, else
   * draft, else this). `undefined` for a caller with no account read to
   * offer (a harness exercising this route for a reason unrelated to
   * AC-1/AC-14): treated identically to every field being empty, never a
   * fabricated value.
   */
  readonly accountDefaults?: AccountReadResult;
  /**
   * AC-14: this section's own saved-but-unconfirmed draft (what a prior
   * "Save and finish later" persisted), read through the working copy
   * port by the caller. Takes precedence over `accountDefaults` but never
   * over a real confirmation -- see `defaultFieldValue`'s own doc
   * comment.
   */
  readonly draftAnswers?: Record<string, unknown>;
  readonly port: WorkingCopyPort;
  /** Called once this section's write has Succeeded — the caller (App.tsx's
   * composition root) folds the new answer into its own `confirmedAnswers`,
   * reloads, and navigates on to change-check. Same signature
   * `MultiSectionStepRoute` already takes, for the same reason. */
  readonly onSectionConfirmed: (sectionId: SectionInstanceId, answers: Record<string, unknown>) => void;
  /** OBJ-106-shaped: called instead of `onSectionConfirmed` when a write
   * comes back Indeterminate. See `MultiSectionStepRouteProps`'s identical
   * prop for the full rationale. */
  readonly onSectionWriteIndeterminate: (sectionId: SectionInstanceId) => void;
  /** m1-portal-ux-fixes Fix D (DEBT-20261023-a7zv): called instead of
   * showing a save-failure panel when a write (confirm OR "Save and finish
   * later") is refused because the renewal is already submitted — the
   * caller routes the session to `/renewal-status`. */
  readonly onRenewalSubmitted: () => void;
  /** Activated by the "Save and finish later" control. Confirmed-to-date
   * state needs no extra write here: every section write already commits
   * the moment its own Continue/Confirm succeeds (ADR-20260821-5wwj — no
   * autosave, no debounce), so this is a plain navigation signal, exactly
   * like `DeclarationRoute`'s own `onSaveAndFinishLater`. */
  readonly onSaveAndFinishLater: () => void;
  /** OBJ-112/OBJ-114/OBJ-116, AC-9: rendered above the form, stale-but-valid
   * data and all, when the caller's post-write re-read has failed. `null`
   * (the ordinary case) renders nothing here — identical contract to
   * `MultiSectionStepRouteProps.reloadFailureBanner`. */
  readonly reloadFailureBanner?: ReactNode;
}

/**
 * AC-12: a blank field shows its own H2 message; `contactEmail`
 * additionally validates shape. Delegates to the shared registry
 * (`validation/error-registry.ts`, ADR-20260918-htmd) rather than
 * authoring a second copy of the messages here — this function's only
 * job is adapting the lifecycle module's `(sectionId, fieldId, value)`
 * signature onto that registry's own `(fieldId, value)` one.
 */
function computeFieldError(_sectionId: SectionInstanceId, fieldId: string, value: string): string | undefined {
  if (!isConfirmDetailsFieldId(fieldId)) {
    return undefined;
  }
  return validateConfirmDetailsField(fieldId, value);
}

export function ConfirmDetailsRoute({
  renewalId,
  stepperItems,
  businessName,
  confirmedAnswers,
  accountDefaults,
  draftAnswers,
  port,
  onSectionConfirmed,
  onSectionWriteIndeterminate,
  onRenewalSubmitted,
  onSaveAndFinishLater,
  reloadFailureBanner = null,
}: Readonly<ConfirmDetailsRouteProps>) {
  const capability = useCapability();
  const isStale = reloadFailureBanner !== null;
  const isConfirmed = isSectionConfirmed(confirmedAnswers, SECTION_ID);

  const lifecycle = useSectionSaveLifecycle({
    renewalId,
    port,
    confirmedAnswers,
    stale: isStale,
    actionLabel: content.continueLabel,
    onSectionConfirmed,
    onSectionWriteIndeterminate,
    onRenewalSubmitted,
    computeFieldError,
    // AC-3, DEBT-20261006-71bn: a section already confirmed at mount is
    // reopened and seeded from the saved answer on the FIRST commit (the
    // hook reads this once, in lazy state initialisers), so there is no
    // non-interactive first frame and no later reseed to overwrite input.
    // Still never re-fires for the in-session "just confirmed, caller's
    // reload is about to land" window -- the customer's own just-typed
    // values are already correct there.
    initialReopen: isConfirmed ? REOPEN_ON_MOUNT : undefined,
  });

  const section = lifecycle.sectionFor(SECTION_ID, FIELDS, isConfirmed ? "confirmed" : "open");

  // Captured ONCE, at mount -- see `initialReopen` above.
  const wasConfirmedOnMountRef = useRef(isConfirmed);

  // AC-1/AC-14: the NOT-confirmed counterpart of `initialReopen` -- seeds
  // every field's EDITABLE value from the draft-else-account-read
  // precedence (`defaultFieldValue`), once, on mount, and only when this
  // instance started out not yet confirmed (`wasConfirmedOnMountRef`,
  // already captured above). Never runs again later in this same mounted
  // instance, for the identical reason `initialReopen` above does not:
  // once the customer has typed anything here, re-seeding would
  // overwrite it with a now-stale default.
  useEffect(() => {
    if (wasConfirmedOnMountRef.current) {
      return;
    }
    for (const fieldId of FIELDS) {
      section.setFieldValue(fieldId, defaultFieldValue(fieldId, draftAnswers, accountDefaults));
    }
    // Runs exactly once, on mount, by design -- see the ref captured above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OBJ-133/FIND-196: a mount that starts NOT confirmed because the
  // caller's own `confirmedAnswers` snapshot is stale (predates an
  // already-committed write -- an Indeterminate write whose caller-side
  // reload lands after this route mounts) must still seed and reopen once
  // that snapshot catches up, exactly as the mount-time case above does,
  // or the form stays permanently empty with no Continue control. This is
  // distinct from the window the comment above protects: THIS effect
  // never fires when the confirm happened on this mounted instance itself
  // (`hasAttemptedConfirmRef`), so the customer's own just-confirmed
  // values are still never overwritten by a redundant reseed.
  const hasAttemptedConfirmRef = useRef(false);
  const prevIsConfirmedRef = useRef(isConfirmed);
  useEffect(() => {
    const wasConfirmed = prevIsConfirmedRef.current;
    prevIsConfirmedRef.current = isConfirmed;
    if (isConfirmed && !wasConfirmed && !hasAttemptedConfirmRef.current) {
      section.onRequestChange();
    }
    // Reacts only to the confirmed/not-confirmed transition -- see the
    // refs captured above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed]);

  // OBJ-C-7/FIND-921: a refused draft write must never be reported to the
  // customer as saved. `null` (the ordinary case) renders nothing below;
  // set only when `writeDraftSection` comes back Failed or Indeterminate,
  // using the SAME content this file's sibling Confirm-write panel renders
  // (`buildSaveOutcomeContent`, `../renewal-capture/outcome-content.ts`),
  // named with THIS screen's own "Continue" control (Fix C,
  // m1-portal-ux-fixes) -- the previously-filed DEBT item asking Content
  // Design to reword the Confirm-authored copy for this action is closed by
  // that fix: the message now names whichever action the caller passes,
  // and this screen's only primary action is "Continue" either way.
  const [draftSaveOutcome, setDraftSaveOutcome] = useState<{
    readonly tone: "error";
    readonly title: string;
    readonly message: string;
  } | null>(null);

  const draftSaveInFlightRef = useRef(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // AC-14, ADR-20260821-5wwj: "Save and finish later" persists whatever the
  // customer has typed so far as this section's DRAFT (never a
  // confirmation) -- one write, on this explicit action, exactly as
  // `ExposuresJourneyRoute`'s own `saveAndFinishLater` does for every
  // exposures section. A port with no draft operation (the optional
  // `writeDraftSection`, ADR-20261016-w8rn's TS-layer exception) simply
  // does not persist, matching every other caller's identical guard --
  // treated the same as a Succeeded write below, since there is nothing
  // to fail.
  async function handleSaveAndFinishLater(): Promise<void> {
    // DEBT-20261022-jp8j: a second activation while the first draft write is
    // still pending is refused. The ref closes the window before React
    // re-renders with the button disabled (two clicks inside one tick);
    // the state is what disables the control for the customer.
    if (draftSaveInFlightRef.current) {
      return;
    }
    draftSaveInFlightRef.current = true;
    setIsSavingDraft(true);
    try {
      await saveDraftAndFinishLater();
    } finally {
      draftSaveInFlightRef.current = false;
      setIsSavingDraft(false);
    }
  }

  async function saveDraftAndFinishLater(): Promise<void> {
    // Fix B: nulled before the write (not only on a Succeeded outcome
    // below) so a SECOND consecutive Failed/Indeterminate outcome always
    // unmounts-then-remounts the outcome panel, same as every other write
    // path in this feature -- see PortalPanelBody's own "mount-only focus"
    // doc comment for why that invariant is what lets it skip a
    // content-keyed re-focus effect.
    setDraftSaveOutcome(null);
    const answers: Record<string, unknown> = {};
    for (const fieldId of FIELDS) {
      answers[fieldId] = section.fieldValue(fieldId);
    }
    const outcome = await port.writeDraftSection?.(renewalId, SECTION_ID, answers);
    if (outcome === undefined || outcome.status === "Succeeded") {
      onSaveAndFinishLater();
      return;
    }
    // Fix D (DEBT-20261023-a7zv): a draft write refused because the
    // renewal is already submitted is never shown as an ordinary save
    // failure -- the session goes straight to the read-only view.
    if (outcome.status === "Failed" && isRenewalSubmittedWriteRefusal(outcome.detail.taxonomyCode)) {
      onRenewalSubmitted();
      return;
    }
    // Failed or Indeterminate: stay on this screen, every typed answer
    // untouched (this function never reads or clears form state on any
    // outcome), and tell the customer nothing was saved rather than
    // navigating them to the "we have saved your responses" landing copy.
    setDraftSaveOutcome(buildSaveOutcomeContent(content.continueLabel)[outcome.status]);
  }

  const renderField = (fieldId: (typeof FIELDS)[number]) => {
    const errorText = section.fieldError(fieldId);
    return (
            <TextField
              key={fieldId}
              id={`confirm-details-${fieldId}`}
              label={getConfirmDetailsFieldLabel(fieldId)}
              value={section.fieldValue(fieldId)}
              onChange={(value) => {
                // AC-12: the phone field keeps only digits, spaces and
                // ( ) + - . as the customer types -- every other field is
                // accepted as entered.
                section.setFieldValue(fieldId, fieldId === "contactPhone" ? sanitizePhoneInput(value) : value);
              }}
              type={FIELD_TYPES[fieldId]}
              // OBJ-100/FIND-124 (AC-3, TG-19): every text field this
              // capture feature renders is formless, so without an
              // explicit autoComplete="off" the browser's own Autofill
              // store persists the last-typed value on the device, keyed
              // by this field's stable id, across customers on a shared
              // device -- the same reasoning `MultiSectionStepRoute.tsx`
              // documents for its own fields, applying identically here
              // regardless of Screen3a's own (pre-this-finding) `auto-
              // complete="organization"`-style hints, which this
              // route deliberately does not carry across.
              autoComplete="off"
              disabled={isStale}
              error={errorText !== undefined}
              testId={`confirm-details-${fieldId}`}
              {...(errorText !== undefined ? { errorText } : {})}
            />
    );
  };

  return (
    <PortalLayout businessName={businessName} headerVariant="flow">
      <Container layout="flow">
        {reloadFailureBanner}
        {stepperItems !== undefined ? <Stepper aria-label="Renewal steps" items={stepperItems} testId="confirm-details-stepper" /> : null}

        {/* H2 (03-RenewalFlow.dc.html, README.md "Screen 3"): screen 3's
            own eyebrow/heading/intro sit INSIDE the card, unlike screen 6's
            (Declaration), which H2 renders above it -- this screen has no
            back link to place above the card at all (it is the first
            step). */}
        <ContentPanel variant="flow">
          <Stack gap="heading">
            <Typography variant="eyebrow" as="p">
              {content.eyebrow}
            </Typography>
            <Typography variant="display" as="h1">
              {content.heading}
            </Typography>
            <Typography variant="bodyLarge">{content.intro}</Typography>
          </Stack>

          <Stack gap="fieldTight">
            {(() => {
              // AC-3: an error summary headed "There is a problem", one link
              // per problem, shown only once Continue has actually been
              // attempted -- `section.fieldError` itself already withholds a
              // value until then (section-save-lifecycle.ts's own
              // `confirmAttempted` gate), so this derives entirely from that,
              // never a second attempted-tracking flag of its own.
              const errorItems = FIELDS.map((fieldId) => {
                const text = section.fieldError(fieldId);
                return text === undefined ? null : { fieldId: `confirm-details-${fieldId}`, text };
              }).filter((item): item is { fieldId: string; text: string } => item !== null);
              return errorItems.length > 0 ? <ErrorSummary items={errorItems} /> : null;
            })()}

            {/* Fix B (m1-portal-ux-fixes): save/server-outcome panels render at
                the TOP of the card, alongside where the error summary above
                renders -- before the fields, never after them. */}
            {section.outcome !== null ? (
              <PortalPanelBody id="confirm-details-outcome" expanded tone="error" title={section.outcome.title} testId="confirm-details-outcome">
                <Typography variant="body">{section.outcome.message}</Typography>
              </PortalPanelBody>
            ) : null}

            {draftSaveOutcome !== null ? (
              <PortalPanelBody
                id="confirm-details-draft-save-outcome"
                expanded
                tone="error"
                title={draftSaveOutcome.title}
                testId="confirm-details-draft-save-outcome"
              >
                <Typography variant="body">{draftSaveOutcome.message}</Typography>
              </PortalPanelBody>
            ) : null}

            {FIELDS.filter((fieldId) => !PAIRED_FIELDS.has(fieldId)).map(renderField)}
            {/* H2 screen 3 sets phone and email side by side. */}
            <FieldRow>{FIELDS.filter((fieldId) => PAIRED_FIELDS.has(fieldId)).map(renderField)}</FieldRow>
          </Stack>

          {/* Business rule 10 (m1-portal-h2-fidelity): no save notice; H2 shows none. */}
          <Stack direction="actions" gap="sm">
              {section.canConfirmBase && capability.confirmableSectionIds.has(SECTION_ID) ? (
                <Button
                  emphasis="primary"
                  onClick={() => {
                    hasAttemptedConfirmRef.current = true;
                    section.onConfirm();
                  }}
                  disabled={section.isSaving}
                  size="large"
                  testId="confirm-details-continue"
                >
                  {content.continueLabel}
                </Button>
              ) : null}
              {capability.canMakeTruthfulnessDeclaration ? (
                <Button
                  emphasis="quiet"
                  onClick={() => {
                    void handleSaveAndFinishLater();
                  }}
                  size="large"
                  disabled={isSavingDraft}
                  testId="confirm-details-save-and-finish-later"
                >
                  {content.saveAndFinishLaterLabel}
                </Button>
              ) : null}
          </Stack>
          {/* H2 screen 3: the help line closes the card, under the actions. */}
          <Typography variant="bodySmall" tone="muted">
            {content.helpLine}
          </Typography>
        </ContentPanel>
      </Container>
    </PortalLayout>
  );
}
